import { createOnboardingList, OnboardingListMutations } from "./createOnboardingList";
import {
	CreatedOnboardingLists,
	hashConfig,
	readCreatedLists,
	writeCreatedLists,
} from "./onboardingCreatedLists";
import { OnboardingListConfig, OnboardingListTypeKey } from "./onboardingListTypes";

type Selections = Partial<Record<OnboardingListTypeKey, OnboardingListConfig>>;

export interface SyncOnboardingListsParams {
	userId: number;
	selections: Selections;
	/** Resolves the (translated) list name for a picked type. */
	listNameFor: (key: OnboardingListTypeKey) => string;
	mutations: OnboardingListMutations;
	deleteList: (id: number) => { unwrap: () => Promise<unknown> };
}

/** Deleting a list the user is replacing must not block the step. */
const deleteQuietly = async (
	deleteList: SyncOnboardingListsParams["deleteList"],
	listId: number
): Promise<void> => {
	try {
		await deleteList(listId).unwrap();
	} catch (err) {
		console.error(err);
	}
};

/**
 * Brings the user's lists in line with their current picks, creating only what
 * actually changed.
 *
 * The Track step can send the user back here, and every Continue used to create
 * a fresh set of lists — so a few round trips left the workspace full of
 * duplicates (PREC-3719). The step therefore remembers each created list id
 * plus the config it came from, and on a repeat Continue it: keeps an unchanged
 * pick as-is, replaces one whose config was edited, deletes one that was
 * unticked, and creates the newly ticked ones.
 */
export const syncOnboardingLists = async ({
	userId,
	selections,
	listNameFor,
	mutations,
	deleteList,
}: SyncOnboardingListsParams): Promise<CreatedOnboardingLists> => {
	const created = readCreatedLists(userId);
	const next: CreatedOnboardingLists = {};

	const persist = () => writeCreatedLists(userId, { ...created, ...next });

	// Untick means "I don't want this list" — drop what was created for it.
	for (const key of Object.keys(created) as OnboardingListTypeKey[]) {
		if (!selections[key]) {
			const existing = created[key];

			delete created[key];

			if (existing) {
				await deleteQuietly(deleteList, existing.listId);
			}

			persist();
		}
	}

	for (const key of Object.keys(selections) as OnboardingListTypeKey[]) {
		const config = selections[key];

		if (!config) {
			continue;
		}

		const configHash = hashConfig(config);
		const existing = created[key];

		if (existing && existing.configHash === configHash) {
			// Same list, but the criteria are re-stamped so an entry written before
			// they were remembered gains them without re-creating the list.
			next[key] = { ...existing, config };

			continue;
		}

		if (existing) {
			delete created[key];

			await deleteQuietly(deleteList, existing.listId);
			persist();
		}

		const listId = await createOnboardingList(config, listNameFor(key), mutations);

		next[key] = { listId, configHash, config };

		// Persisted per list, so a failure halfway through still records what
		// exists — the next Continue reuses it instead of duplicating it.
		persist();
	}

	writeCreatedLists(userId, next);

	return next;
};
